/** Cookie that remembers the language a visitor picked on the booking pages. */
export const BOOKING_LANGUAGE_COOKIE = "cal-booking-lang";
export const BOOKING_LANGUAGE_PARAM = "lang";
const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;
export const BOOKING_LANGUAGE_COOKIE_MAX_AGE = ONE_YEAR_IN_SECONDS;

export type BookingLocaleSource = "param" | "cookie" | "browser" | "default";

const normalize = (locale: string) => locale.trim().replace("_", "-");
const baseOf = (locale: string) => locale.split("-")[0].toLowerCase();

/**
 * Parses a comma-separated locale list such as "de,en,pl". Keeps supported locales only,
 * in the given order and without duplicates. The first entry is the default language.
 */
export function parseBookingLanguages(
  value: string | undefined | null,
  supportedLocales: readonly string[]
): string[] {
  if (!value) return [];
  const result: string[] = [];
  for (const raw of value.split(",")) {
    const locale = matchLocale(normalize(raw), supportedLocales);
    if (locale && !result.includes(locale)) result.push(locale);
  }
  return result;
}

/**
 * Finds `requested` in `available`: the exact locale first (case-insensitive), then its base
 * language, so "en-US" matches "en" and "pt" matches "pt-BR" only when no plain "pt" exists.
 */
export function matchLocale(
  requested: string | null | undefined,
  available: readonly string[]
): string | null {
  if (!requested) return null;
  const wanted = normalize(requested).toLowerCase();
  if (!wanted) return null;
  const exact = available.find((locale) => locale.toLowerCase() === wanted);
  if (exact) return exact;
  const base = baseOf(wanted);
  return (
    available.find((locale) => locale.toLowerCase() === base) ??
    available.find((locale) => baseOf(locale) === base) ??
    null
  );
}

/** Locale tags from an Accept-Language header, highest quality first; "*" and q=0 are dropped. */
export function parseAcceptLanguageHeader(header: string): string[] {
  return header
    .split(",")
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(";");
      const qualityParam = params.find((param) => param.trim().startsWith("q="));
      const quality = qualityParam ? Number.parseFloat(qualityParam.trim().slice(2)) : 1;
      return { tag: tag.trim(), quality: Number.isNaN(quality) ? 0 : quality, index };
    })
    .filter(({ tag, quality }) => tag && tag !== "*" && quality > 0)
    .sort((a, b) => b.quality - a.quality || a.index - b.index)
    .map(({ tag }) => tag);
}

export function getLanguageFromAcceptLanguage(
  acceptLanguage: string | null | undefined,
  available: readonly string[]
): string | null {
  if (!acceptLanguage) return null;
  for (const tag of parseAcceptLanguageHeader(acceptLanguage)) {
    const match = matchLocale(tag, available);
    if (match) return match;
  }
  return null;
}

/**
 * Picks the booking page language: a valid `?lang` value, then the visitor's saved choice,
 * then the browser language, then the event's or the site's default. Returns null when no
 * booking languages are configured, so pages keep their previous behavior.
 */
export function resolveBookingLocale({
  languages,
  langParam,
  savedLocale,
  acceptLanguage,
  eventDefaultLocale,
}: {
  languages: readonly string[];
  langParam?: string | string[] | null;
  savedLocale?: string | null;
  acceptLanguage?: string | null;
  eventDefaultLocale?: string | null;
}): { locale: string; source: BookingLocaleSource } | null {
  if (!languages.length) return null;

  const param = Array.isArray(langParam) ? langParam[0] : langParam;
  const fromParam = matchLocale(param, languages);
  if (fromParam) return { locale: fromParam, source: "param" };

  const fromCookie = matchLocale(savedLocale, languages);
  if (fromCookie) return { locale: fromCookie, source: "cookie" };

  const fromBrowser = getLanguageFromAcceptLanguage(acceptLanguage, languages);
  if (fromBrowser) return { locale: fromBrowser, source: "browser" };

  return { locale: matchLocale(eventDefaultLocale, languages) ?? languages[0], source: "default" };
}

/** Returns `href` with only the `lang` parameter changed, keeping every other parameter and the hash. */
export function withBookingLanguage(href: string, locale: string): string {
  const url = new URL(href, "http://placeholder.invalid");
  url.searchParams.set(BOOKING_LANGUAGE_PARAM, locale);
  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(href);
  return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}

/** Native language name, capitalized: "Deutsch", "English", "Polski". */
export function getNativeLanguageName(locale: string): string {
  let name = locale;
  try {
    name = new Intl.DisplayNames([locale], { type: "language" }).of(locale) ?? locale;
  } catch {
    // An unknown locale code keeps its code as the label
  }
  return name.charAt(0).toUpperCase() + name.slice(1);
}
